import {
  BRAIN_SIGNAL_TEMPLATES,
  BrainSignalDefinitionStatus,
  BrainSignalNotifyChannel,
  type BrainSignalTemplate,
  type BrainSignalTemplateArea,
} from '@this+that/models';
import type { TFunction } from 'i18next';

import { resolveSignalTemplateTexts } from './brainSignalTemplateTexts';
import type {
  BrainSignalDefinitionSaveInput,
  BrainSignalDefinitionView,
  BrainSignalPolicyForm,
  BrainSignalTemplateOverridesInput,
  BrainSignalView,
} from '../../../../../../../../models/brainSignals';
import { BRAIN_SIGNAL_AREA_ORDER } from '../brainPanel.constants';

export interface BrainSignalTemplateGroup {
  area: BrainSignalTemplateArea;
  templates: BrainSignalTemplate[];
}

export function groupTemplatesByArea(
  templates: readonly BrainSignalTemplate[],
): BrainSignalTemplateGroup[] {
  return BRAIN_SIGNAL_AREA_ORDER.map((area) => ({
    area,
    templates: templates.filter((template) => template.area === area),
  })).filter(({ templates: inArea }) => inArea.length > 0);
}

export function toPolicyForm(
  definition: BrainSignalDefinitionView,
): BrainSignalPolicyForm {
  const { policy, actions } = definition.active;
  return {
    reviewBeforeAction: policy.reviewBeforeAction,
    emailEnabled: actions.notify.channels.includes(
      BrainSignalNotifyChannel.EMAIL,
    ),
    minLevel: actions.notify.minLevel,
    cooldownHours: policy.cooldownHours,
  };
}

const TYPENAME_KEY = '__typename';

function withoutNulls<T extends object>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry != null),
  ) as T;
}

function withoutNullsOrTypename<T extends object>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([key, entry]) => entry != null && key !== TYPENAME_KEY,
    ),
  ) as T;
}

/** Rebuilds the full save input from the active version; only the form's fields change, and no `__typename` leaks into it. */
export function buildDefinitionSaveInput(
  definition: BrainSignalDefinitionView,
  form: BrainSignalPolicyForm,
): BrainSignalDefinitionSaveInput {
  const { active } = definition;
  const { semantic } = active;
  return {
    defId: definition.defId,
    name: active.name,
    description: active.description ?? undefined,
    lang: active.lang,
    scope: { entityTypes: active.scope.entityTypes },
    match: withoutNulls({
      eventKinds: active.match.eventKinds,
      categories: active.match.categories,
      predicates: active.match.predicates,
      entityTypes: active.match.entityTypes,
      entityIds: active.match.entityIds,
      areaKeys: active.match.areaKeys,
      classifications: active.match.classifications,
      sourceTypes: active.match.sourceTypes,
    }),
    rules: active.rules.map(withoutNullsOrTypename),
    policy: withoutNulls({
      cooldownHours: form.cooldownHours,
      decayDays: active.policy.decayDays,
      closeOnResolution: active.policy.closeOnResolution,
      reviewBeforeAction: form.reviewBeforeAction,
      maxEvaluationsPerDay: active.policy.maxEvaluationsPerDay,
      minConfidence: active.policy.minConfidence,
      minDistinctSources: active.policy.minDistinctSources,
    }),
    notify: {
      userIds: active.actions.notify.userIds,
      roles: active.actions.notify.roles,
      channels: form.emailEnabled
        ? [BrainSignalNotifyChannel.IN_APP, BrainSignalNotifyChannel.EMAIL]
        : [BrainSignalNotifyChannel.IN_APP],
      minLevel: form.minLevel,
    },
    semantic: semantic
      ? {
          condition: semantic.condition,
          depth: semantic.depth,
          levels: semantic.levels.map(({ level, description }) => ({
            level,
            description,
          })),
        }
      : undefined,
  };
}

export function findTemplateDefinition(
  definitions: readonly BrainSignalDefinitionView[],
  templateId: string,
): BrainSignalDefinitionView | undefined {
  return definitions.find(
    (definition) =>
      definition.templateId === templateId &&
      definition.status !== BrainSignalDefinitionStatus.ARCHIVED,
  );
}

/** The definition's own name, else its template's name key; null when neither is known. */
export function resolveSignalName(
  signal: Pick<BrainSignalView, 'defId'>,
  definitions: readonly BrainSignalDefinitionView[],
): { text: string } | { key: string } | null {
  const definition = definitions.find(({ defId }) => defId === signal.defId);
  if (!definition) {
    return null;
  }
  if (definition.active.name) {
    return { text: definition.active.name };
  }
  const template = BRAIN_SIGNAL_TEMPLATES.find(
    ({ templateId }) => templateId === definition.templateId,
  );
  return template ? { key: template.nameKey } : null;
}

export function buildSignalTemplateOverrides(
  t: TFunction,
  template: BrainSignalTemplate,
  lang: string,
): BrainSignalTemplateOverridesInput {
  return { ...resolveSignalTemplateTexts(t, template), lang };
}
